@if (isAllLoaded$ | async) {
  <ng-container *ngVar="(isEmptyState$ | async) as isEmptyState">
    @if (isLoadingFailed$ | async) {
      <ng-container *ngTemplateOutlet="loadingFailedState"></ng-container>
    } @else if (isEmptyState === false) {
      <ng-container *ngTemplateOutlet="preview"></ng-container>
    } @else if (isEmptyState) {
      <ng-container *ngTemplateOutlet="emptyState"></ng-container>
    }
  </ng-container>
} @else {
  <div class="w-full h-full bg-navy-30">
    @if (editing$ | async) {
      <app-loading-animation [width]="'270px'" [height]="'270px'"></app-loading-animation>
    } @else {
      <app-generic-loading-animation></app-generic-loading-animation>
    }
  </div>
}

@if (isPreviewLink) {
  <app-preview-link-customer></app-preview-link-customer>
}

<ng-template #preview>
  <div
    *ngVar="editing$ | async as editing"
    class="flex flex-col relative w-full"
    [class.fullscreen]="isPreviewMode$ | async"
    [class.published-page-container]="!editing"
  >
    <div #dsClickWrap id="ds-clickwrap"></div>
  
    @if (docusignSigningUrl$ | async; as docusignUrl) {
      <div class="iframe-container">
        <iframe #signingFrame class="w-250 h-200" [src]="docusignUrl"></iframe>
      </div>
    }

    <div class="flex flex-col items-center w-full">
      @if (editing) {
        <div
          *ngVar="hasTrustCenterUrl$ | async as hasTrustCenterUrl"
          class="sticky w-full z-10 top-0 flex flex-row p-3 gap-3 items-center border-b border-solid border-navy-40 bg-white"
        >
          <div
            [ngbTooltip]="!hasTrustCenterUrl ? freemiumTitleTooltip : null"
            [keepOnHover]="true"
            [placement]="'bottom'"
            [tooltipClass]="'default-tooltip w-54'"
          >
            <app-new-button
              data-test-id="publish-button"
              [svgIconPath]="'upload-to-cloud'"
              [size]="'responsive'"
              [disabled]="!hasTrustCenterUrl"
              (click)="openPublishConfirmation()"
            >
              {{ buildTranslationKey('publish') | translate }}
            </app-new-button>
          </div>

          @if (lastPublished$ | async; as lastPublished) {
            <app-last-published-info
              [lastPublished]="lastPublished"
              [hasPendingChanges]="hasPendingChanges$ | async">
            </app-last-published-info>
          }

          <app-new-button
            [svgIconPath]="'eye_white'"
            [size]="'responsive'"
            [type]="'secondary'"
            (click)="onPreviewBtnClick()"
          >
            {{ buildTranslationKey('previewBtn') | translate }}
          </app-new-button>

          <div
            [ngbTooltip]="!hasTrustCenterUrl ? freemiumTitleTooltip : null"
            [keepOnHover]="true"
            [placement]="'bottom'"
            [tooltipClass]="'default-tooltip w-54'"
          >
            <app-new-button
              class="trust-center-btn"
              [svgIconPath]="'newscreen'"
              [size]="'responsive'"
              [type]="'secondary'"
              [disabled]="!hasTrustCenterUrl"
              (click)="navigateToPublishedPage()"
            >
              {{ buildTranslationKey('trustCenterBtn') | translate }}
            </app-new-button>
          </div>
        </div>
      }

      @if (isPreviewMode$ | async) {
        <div
          *ngVar="hasTrustCenterUrl$ | async as hasTrustCenterUrl"
          data-test-id="viewer-side-menu"
          class="sticky w-full z-10 top-0 flex flex-row p-3 gap-3 items-center bg-blue-90-15"
        >
          <div class="pr-3 border-r border-solid border-navy-40">
            <app-new-button
              [size]="'responsive'"
              [type]="'secondary'"
              [svgIconPath]="'arrow-left'"
              (click)="onStopPreviewBtnClick()"
            >
              {{ buildTranslationKey(isBackToBrandingTab ? 'backToBranding' : 'backToEditor') | translate }}
            </app-new-button>
          </div>

          <app-new-button
            [svgIconPath]="'upload-to-cloud'"
            [disabled]="!hasTrustCenterUrl"
            [size]="'responsive'"
            (click)="openPublishConfirmation()"
          >
            {{ buildTranslationKey('publishNow') | translate }}
          </app-new-button>

          <span class="text-4xl font-bold text-navy-90">
            {{ buildTranslationKey('trustCenterPreviewMode') | translate }}
          </span>
        </div>
      }

      <div class="preview-container w-full flex justify-center items-start">
        <div class="flex flex-col w-full">
          <div class="flex flex-row py-8 px-6 justify-between gap-4">
            <ng-container *ngTemplateOutlet="trustCenterLogo"></ng-container>

            @if (editing === false) {
              <ng-container *ngTemplateOutlet="externalUserActions"></ng-container>
            }
          </div>

          <div class="px-6 pb-9 md:pb-6">
            @if (editing === false && hasPendingAccess$ | async) {
              <app-tip class="pending-access-tip" [tipId]="'pending-access-tip'">
                {{ buildTranslationKey('pendingAccessTip') | translate }}
              </app-tip>
            }
            <div class="shadow-sm flex gap-7.5 flex-col border bg-white rounded-xl border-solid border-navy-50">
              <app-overview class="px-7.5 pt-7.5"></app-overview>
              <app-frameworks></app-frameworks>
            </div>
          </div>

          <div class="content-container" [class.published]="isPublishedPage">
            @if (tabs$ | async; as tabs) {
              @if (currentTabId && tabs.length) {
                <div *showOnScreen="'md'" class="flex justify-center px-5 no-scrollbar">
                  <app-tabs
                    class="main-tabs w-full md:w-auto"
                    [tabs]="tabs"
                    [selectTabById]="currentTabId"
                    [allowTabTemplates]="false"
                    [borderBottom]="true"
                    (tabChange)="onTabChange($event)"
                  ></app-tabs>
                </div>

                <app-dropdown-control
                  *hideOnScreen="'md'"
                  class="block px-3"
                  [selectedValue]="{ tabId: currentTabId }"
                  [idSelector]="tabIdSelector"
                  [displayValueSelector]="tabDisplaySelector.bind(this)"
                  [data]="tabs"
                  [visibleItemsCount]="5"
                  [showSelectedOptionIcon]="true"
                  (selectClick)="onTabChange($event.tabId)"
                ></app-dropdown-control>
              }
            }

            @switch (currentTabId) {
              @case (Tabs.Documents) {
                <app-documents class="flex-1 px-6"></app-documents>
              }
              @case (Tabs.Controls) {
                <app-controls class="flex-1"></app-controls>
              }
              @case (Tabs.Subprocessors) {
                <app-subprocessors class="flex-1 px-5 pb-5"></app-subprocessors>
              }
              @case (Tabs.Policies) {
                <app-policies class="flex-1"></app-policies>
              }
              @case (Tabs.FAQs) {
                <app-faqs class="flex-1 px-5 pb-5"></app-faqs>
              }
            }

            @if (!editing) {
              <app-powered-by class="w-full mb-8" [color]="'secondary'" [size]="'medium'"></app-powered-by>
            }
          </div>
        </div>
      </div>
    </div>
  </div>

  @if (isNdaLoading) {
    <div class="nda-loader">
      <svg-icon class="w-25 h-25 animate-spin" [src]="'circle-loader'"></svg-icon>

      <span>{{ buildTranslationKey('loadingNda') | translate }}</span>
    </div>
  }
</ng-template>

<ng-template #emptyState>
  <div class="w-full h-full flex flex-col justify-center items-center gap-4">
    <svg-icon class="w-60" [src]="'trust-center-coming-soon'"></svg-icon>

    <span class="font-main text-8xl font-bold text-navy-90 text-center">
      {{ buildTranslationKey('comingSoon') | translate }}
    </span>
  </div>
</ng-template>

<ng-template #loadingFailedState>
  <div class="w-full h-full flex flex-col justify-center items-center">
    <svg-icon class="w-32 mb-4" [src]="'error-trust'"></svg-icon>

    <span class="font-main text-4xl font-bold text-navy-90 text-center mb-1">
      {{ buildTranslationKey('trustCenterErrorTitle') | translate }}
    </span>
    
    <span class="font-main text-2xl text-navy-90 text-center">
      {{ buildTranslationKey('trustCenterErrorDescription') | translate }}
    </span>
  </div>
</ng-template>

<ng-template #trustCenterLogo>
  <div
    *ngVar="{ logoUrl: (trustCenterInfo$ | async), isEditing: (editing$ | async) } as data"
    class="flex justify-center items-center gap-5"
  >
    @if (data.logoUrl?.full_logo_url) {
      <img class="max-h-14 w-auto" [src]="data.logoUrl?.full_logo_url"/>
    } @else {
      @if (data.isEditing) {
        <div class="group cursor-pointer">
          <div
            class="rounded-md border border-navy-90 border-dashed bg-white h-14 w-40 flex justify-center items-center text-navy-90 text-lg cursor-pointer"
            (click)="navigateToSettingsBrandingTab()"
          >
            {{ buildTranslationKey('myLogo') | translate }}
          </div>
        </div>
      }
    }

    @if (data.isEditing) {
      <app-icon-button
        class="edit-button group-hover:bg-navy-40 w-8 h-8"
        [svgIconPath]="'edit'"
        [type]="'basic'"
        [iconSize]="'small'"
        (click)="navigateToSettingsBrandingTab()"
      ></app-icon-button>
    }
  </div>
</ng-template>

<ng-template #externalUserActions>
  <div
    *ngVar="{
      loggedIn: (loggedIn$ | async),
      hasPendingAccess: (hasPendingAccess$ | async),
      isPreviewMode: (isPreviewMode$ | async)
    } as data"
    class="flex items-center">
    <div class="flex gap-3 items-center">
      @if (data.isPreviewMode) {
        <ng-container *ngTemplateOutlet="requestAccessBtn; context: { isPreviewMode: data.isPreviewMode }"></ng-container>
      } @else {
        @if (data.loggedIn) {
          <ng-container *ngTemplateOutlet="logoutBtn"></ng-container>
        } @else if (data.loggedIn === false) {
          @if (data.hasPendingAccess) {
            <ng-container *ngTemplateOutlet="pendingAccessBtn"></ng-container>
          } @else if (data.hasPendingAccess === false) {
            <ng-container *ngTemplateOutlet="requestAccessBtn"></ng-container>
          }
        }
      }
    </div>
  </div>
</ng-template>

<ng-template #logoutBtn>
  <app-action-menu-button
    class="logout-btn text-navy-90 font-bold whitespace-nowrap"
    [icon]="'profile-mask-dark'"
    [type]="'basic'"
    [buttonSize]="'responsive'"
    [buttonTextTranslationKey]="username$ | async"
    [menuActions]="loggedInMenuActions"
    [listWidth]="'asDropdownButton'"
    [displayArrow]="true"
    [rotateArrow]="false"
  ></app-action-menu-button>
</ng-template>

<ng-template #requestAccessBtn let-isPreviewMode="isPreviewMode">
  <app-new-button
    class="request-access-button svg-white whitespace-nowrap"
    [svgIconPath]="'lock'"
    [size]="'responsive'"
    (click)="isPreviewMode ? undefined : requestAccess()">
    {{ buildTranslationKey('requestAccess') | translate }}
  </app-new-button>
</ng-template>

<ng-template #pendingAccessBtn>
  <div
    [ngbTooltip]="buildTranslationKey('pendingAccessTooltip') | translate"
    [keepOnHover]="true"
    [placement]="'bottom'"
    [tooltipClass]="'default-tooltip w-54'"
  >
    <app-new-button class="pending-access-btn" [disabled]="true" [type]="'secondary'" [svgIconPath]="'lock'">
      {{ buildTranslationKey('pendingAccess') | translate }}
    </app-new-button>
  </div>
</ng-template>

<ng-template #freemiumTitleTooltip>
  <div>
    <span>{{ buildTranslationKey('header.freemiumTooltipHeader') | translate }}</span>
    <span class="cursor-pointer underline" (click)="contactUs()">{{ buildTranslationKey('header.contactUs') | translate }}</span>
    <span>{{ buildTranslationKey('header.freemiumTooltipDescription') | translate }}</span>
  </div>
</ng-template>
